import {
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Heading,
  Flex,
  Button,
  Divider,
  Text,
  ModalFooter,
  FormErrorMessage,
  FormControl,
  Link,
  Icon,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RiExternalLinkFill } from 'react-icons/ri';

import { FormikTextArea } from 'components/Formik';
import { ControllableModal } from 'components/Modal';
import RadioInput from 'components/RadioCard';
import { caliBreakComplianceOptions } from 'containers/CompanySettings/shared/options';
import { useUserObject } from 'store/useUserStore';

import { CABreakComplianceFormValidationSchema, initialCABreakComplianceFormData } from './schema';

type TCABreakComplianceModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  breakData: {
    expectedMealBreak: number;
    totalMealBreak: number;
    expectedRestBreak: number;
    totalRestBreak: number;
  };
  handleAddCABreak: (breakType: 'meal' | 'rest') => void;
  handleCABreakComplianceFormSubmit: (values: typeof initialCABreakComplianceFormData) => void;
};

const CABreakComplianceModal: React.FC<TCABreakComplianceModalProps> = ({
  isOpen,
  isLoading,
  breakData,
  handleCABreakComplianceFormSubmit,
  onClose,
  handleAddCABreak,
}) => {
  const { t } = useTranslation(['common', 'employees', 'yup']);
  const currentUser = useUserObject();

  return (
    <ControllableModal isOpen={isOpen} onClose={onClose} isCentered={false}>
      <ModalContent>
        <ModalHeader>{t('employees:breakCompliance')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Box>
            <Formik
              enableReinitialize
              initialValues={initialCABreakComplianceFormData}
              validationSchema={CABreakComplianceFormValidationSchema}
              onSubmit={handleCABreakComplianceFormSubmit}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Form noValidate autoComplete="off">
                  <Heading as="h5" size="md" textAlign="center" mb="4">
                    Meal Break
                  </Heading>
                  <Flex alignItems="flex-start" justifyContent="space-between" mb="4">
                    <Box>
                      <Text>
                        {t('employees:expectedMealBreak')}: <Text as="strong">{breakData.expectedMealBreak}</Text>
                      </Text>
                      <Text color="secondary.600">
                        {t('employees:yourMealBreak')}: <Text as="strong">{breakData.totalMealBreak.toFixed(2)}</Text>
                      </Text>
                    </Box>
                    <Button type="button" size="sm" onClick={() => handleAddCABreak('meal')}>
                      {t('employees:addMealBreak')}
                    </Button>
                  </Flex>

                  <Box textAlign="center" mb="2">
                    <Box mb="2">
                      <Text fontWeight="bold">{currentUser?.assignedCaliBreakRule?.meal_break_title}</Text>
                      <Text fontSize="sm">{currentUser?.assignedCaliBreakRule?.meal_break_desc}</Text>
                      {currentUser?.assignedCaliBreakRule?.meal_break_url && (
                        <Button
                          as={Link}
                          variant="link"
                          href={currentUser?.assignedCaliBreakRule?.meal_break_url}
                          isExternal
                          mt="1"
                          rightIcon={<Icon as={RiExternalLinkFill} />}
                        >
                          Break Policy
                        </Button>
                      )}
                    </Box>
                    <FormControl
                      display="flex"
                      alignItems="center"
                      flexDirection="column"
                      isInvalid={touched.mealBreakCompliance && errors.mealBreakCompliance ? true : false}
                      mb="4"
                    >
                      <RadioInput
                        variant="secondary"
                        options={caliBreakComplianceOptions}
                        name="mealBreakCompliance"
                        defaultValue={values.mealBreakCompliance}
                        handleChange={(e: any) => {
                          setFieldValue('mealBreakCompliance', e);
                        }}
                      />

                      {touched.mealBreakCompliance && errors?.mealBreakCompliance ? (
                        <FormErrorMessage fontSize="13px" mt="1">
                          {/* @ts-ignore */}
                          {t(errors?.mealBreakCompliance?.msg)}
                        </FormErrorMessage>
                      ) : null}
                    </FormControl>
                  </Box>

                  <FormikTextArea name="mealBreakReason" label={t('employees:notesMealBreak')} />
                  <Divider my="6" />

                  <Heading as="h5" size="md" textAlign="center" mb="4">
                    Rest Break
                  </Heading>
                  <Flex alignItems="flex-start" justifyContent="space-between" mb="4">
                    <Box>
                      <Text>
                        {t('employees:expectedRestBreak')}: <Text as="strong">{breakData.expectedRestBreak}</Text>
                      </Text>
                      <Text color="secondary.600">
                        {t('employees:yourRestBreak')}: <Text as="strong">{breakData.totalRestBreak.toFixed(2)}</Text>
                      </Text>
                    </Box>
                    <Button type="button" size="sm" display="block" onClick={() => handleAddCABreak('rest')}>
                      {t('employees:addRestBreak')}
                    </Button>
                  </Flex>

                  <Box textAlign="center" mb="2">
                    <Box mb="2">
                      <Text fontWeight="bold">{currentUser?.assignedCaliBreakRule?.rest_break_title}</Text>
                      <Text fontSize="sm">{currentUser?.assignedCaliBreakRule?.rest_break_desc}</Text>
                      {currentUser?.assignedCaliBreakRule?.rest_break_url && (
                        <Button
                          as={Link}
                          variant="link"
                          href={currentUser?.assignedCaliBreakRule?.rest_break_url}
                          isExternal
                          mt="1"
                          rightIcon={<Icon as={RiExternalLinkFill} />}
                        >
                          Break Policy
                        </Button>
                      )}
                    </Box>
                    <FormControl
                      display="flex"
                      alignItems="center"
                      flexDirection="column"
                      isInvalid={touched.restBreakCompliance && errors.restBreakCompliance ? true : false}
                      mb="4"
                    >
                      <RadioInput
                        variant="secondary"
                        options={caliBreakComplianceOptions}
                        name="restBreakCompliance"
                        defaultValue={values.restBreakCompliance}
                        handleChange={(e: any) => {
                          setFieldValue('restBreakCompliance', e);
                        }}
                      />

                      {touched.restBreakCompliance && errors?.restBreakCompliance ? (
                        <FormErrorMessage fontSize="13px" mt="1">
                          {/* @ts-ignore */}
                          {t(errors?.restBreakCompliance?.msg)}
                        </FormErrorMessage>
                      ) : null}
                    </FormControl>
                  </Box>

                  <FormikTextArea name="restBreakReason" label={t('employees:notesRestBreak')} />
                  <ModalFooter p={2}>
                    <Flex justifyContent="flex-end" gridGap={3}>
                      <Button colorScheme="secondary" variant="outline" onClick={onClose}>
                        {t('common:cancel')}
                      </Button>
                      <Button colorScheme="secondary" type="submit" isLoading={isLoading}>
                        {t('common:save')}
                      </Button>
                    </Flex>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </Box>
        </ModalBody>
      </ModalContent>
    </ControllableModal>
  );
};

export default CABreakComplianceModal;
